import { useState, useEffect } from "react";
import {
  BookOpen, TrendingUp, BarChart3, Trophy, Users, Calendar, Loader2, AlertTriangle
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { apiService, schoolApi } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { useLang } from "@/hooks/useLang";

// 🔧 Helper robuste pour extraire les données quel que soit le format de réponse
function extractData(response: any): any {
  if (!response) return null;
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (response.data !== undefined) return response.data;
  if (Array.isArray(response.items)) return response.items;
  if (typeof response === "object" && !Array.isArray(response)) return response;
  return response;
}

// ── Petit tableau Top/Bottom réutilisé ──
function RankTable({ title, titleClass, rankClass, rows, noData }: {
  title: string; titleClass: string; rankClass: string; rows: any[]; noData: string;
}) {
  return (
    <div>
      <h4 className={`text-sm font-semibold mb-3 ${titleClass}`}>{title}</h4>
      {rows.length > 0 ? (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b sticky top-0 bg-white">
                <th className="py-1.5 pr-2 w-8">#</th>
                <th className="py-1.5 pr-2 w-24">Matricule</th>
                <th className="py-1.5 pr-2">Nom</th>
                <th className="py-1.5 text-right">Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s: any, i: number) => (
                <tr key={`${s.id}-${i}`} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className={`py-1.5 pr-2 font-bold ${rankClass}`}>{i + 1}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs text-gray-600">{s.matricule}</td>
                  <td className="py-1.5 pr-2 text-gray-900">{s.name}</td>
                  <td className="py-1.5 text-right font-bold text-gray-900">{s.score.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-32 flex items-center justify-center text-gray-400 text-sm">{noData}</div>
      )}
    </div>
  );
}

export default function GradesDashboardPage() {
  const { lang } = useLang();
  const { user } = useAuthStore();

  const t = {
    fr: {
      title: 'Tableau de bord - Notes',
      subtitle: "Vue d'ensemble des performances scolaires",
      totalStudents: 'Élèves',
      totalGrades: 'Notes saisies',
      avgGeneral: 'Moy. générale',
      sequences: 'Séquences',
      recentActivity: 'Activité récente',
      topStudents: 'Meilleurs élèves',
      myClasses: 'Mes classes et matières',
      top10: '10 meilleurs',
      bottom10: '10 derniers',
      colGrade: 'Note',
      distribution: 'Répartition des notes',
      bySubject: 'Par matière',
      loading: 'Chargement...',
      noData: 'Aucune donnée',
      noDataHint: 'Commencez par créer des séquences et saisir des notes.',
      noSchool: 'Aucun établissement associé à votre compte.',
      noYear: 'Aucune année scolaire active pour cet établissement.',
      yearLabel: 'Année scolaire',
      schoolLabel: 'Établissement',
    },
    en: {
      title: 'Dashboard - Grades',
      subtitle: 'Overview of academic performance',
      totalStudents: 'Students',
      totalGrades: 'Grades entered',
      avgGeneral: 'Gen. avg',
      sequences: 'Sequences',
      recentActivity: 'Recent activity',
      topStudents: 'Top students',
      myClasses: 'My classes and subjects',
      top10: 'Top 10',
      bottom10: 'Bottom 10',
      colGrade: 'Grade',
      distribution: 'Grade distribution',
      bySubject: 'By subject',
      loading: 'Loading...',
      noData: 'No data',
      noDataHint: 'Start by creating sequences and entering grades.',
      noSchool: 'No school associated with your account.',
      noYear: 'No active academic year for this school.',
      yearLabel: 'Academic year',
      schoolLabel: 'School',
    }
  }[lang];

  const [stats, setStats] = useState({
    totalStudents: 0,
    totalGrades: 0,
    avgGeneral: 0,
    sequences: 0,
  });
  const [topStudents, setTopStudents] = useState<any[]>([]);
  const [gradeDistribution, setGradeDistribution] = useState<any[]>([]);
  const [subjectAverages, setSubjectAverages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string>('');
  // ── Top 10 / derniers 10 par classe-matière attribuée ──
  const [teacherAssignments, setTeacherAssignments] = useState<any[]>([]);
  const [selectedAssignIdx, setSelectedAssignIdx] = useState(0);
  const [topBottom, setTopBottom] = useState<{ top: any[]; bottom: any[] }>({ top: [], bottom: [] });
  const [tbLoading, setTbLoading] = useState(false);

  // 🔧 Année scolaire active et établissement
  const [currentYear, setCurrentYear] = useState<any>(null);
  const [yearLoading, setYearLoading] = useState(true);

  // 🔧 Résolution en CASCADE du school_id : user.school_id → fiche teacher →
  // school_id sélectionné dans le header → null. Les comptes enseignants n'ont
  // souvent PAS de user.school_id (c'est la fiche teachers qui porte l'établissement)
  // → avant ce fix, tout le dashboard tombait sur « Aucun établissement associé ».
  const [effectiveSchoolId, setEffectiveSchoolId] = useState<number | null>(user?.school_id ?? null);
  const [teacherId, setTeacherId] = useState<number | null>((user as any)?.teacher_id ?? (user as any)?.teacher?.id ?? null);
  const [profileLoading, setProfileLoading] = useState(true);
  const schoolId = effectiveSchoolId;

  useEffect(() => {
    const resolveProfile = async () => {
      let sid: number | null = user?.school_id ?? null;
      let tid: number | null = (user as any)?.teacher_id ?? (user as any)?.teacher?.id ?? null;
      if (user?.id && (!sid || !tid)) {
        try {
          const res: any = await apiService.get('/teachers', { params: { user_id: user.id } });
          const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : Array.isArray(res?.data) ? res.data : []);
          const t = list.find((x: any) => Number(x.user_id) === Number(user.id)) || list[0];
          if (t) {
            sid = sid ?? (t.school_id != null ? Number(t.school_id) : null);
            tid = tid ?? (t.id != null ? Number(t.id) : null);
          }
        } catch { /* profil teacher indisponible */ }
      }
      if (!sid) {
        try {
          const stored = localStorage.getItem('lybibal-selected-school-id');
          if (stored && !isNaN(Number(stored))) sid = Number(stored);
        } catch { /* ignore */ }
      }
      setEffectiveSchoolId(sid);
      setTeacherId(tid);
      setProfileLoading(false);
    };
    resolveProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Charger l'année scolaire active
  useEffect(() => {
    const loadCurrentYear = async () => {
      if (!schoolId) {
        setYearLoading(false);
        return;
      }
      setYearLoading(true);
      try {
        const res = await schoolApi.getCurrentAcademicYear(schoolId).catch(() => null);
        const yearData = extractData(res);
        setCurrentYear(yearData);
      } catch (e) {
        console.error('Error loading current year:', e);
      } finally {
        setYearLoading(false);
      }
    };
    loadCurrentYear();
  }, [schoolId]);

  useEffect(() => {
    const loadDashboard = async () => {
      // Attendre que l'année soit chargée
      if (yearLoading) return;

      // Pas d'établissement ou pas d'année → pas de données
      if (!schoolId || !currentYear) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setDebugInfo('');

      const yearId = currentYear.id;
      const params = { school_id: schoolId, academic_year_id: yearId };

      try {
        // Load stats from various endpoints avec filtres
        const [studentsRes, gradesRes, seqRes] = await Promise.all([
          apiService.get('/students/stats/summary', { params }).catch((e: any) => {
            console.log('[DEBUG] students/stats/summary error:', e?.response?.status, e?.message);
            return { data: {} };
          }),
          apiService.get('/grades', { params }).catch((e: any) => {
            console.log('[DEBUG] grades error:', e?.response?.status, e?.message);
            return { data: [] };
          }),
          apiService.get('/grades/sequences', { params }).catch((e: any) => {
            console.log('[DEBUG] sequences error:', e?.response?.status, e?.message);
            return { data: [] };
          }),
        ]);

        // 🔍 DEBUG logs
        console.log('[DEBUG] studentsRes raw:', studentsRes);
        console.log('[DEBUG] gradesRes raw:', gradesRes);
        console.log('[DEBUG] seqRes raw:', seqRes);

        const studentsData = extractData(studentsRes) || {};
        const gradesDataRaw = extractData(gradesRes);
        const gradesData = Array.isArray(gradesDataRaw) ? gradesDataRaw : [];
        const seqDataRaw = extractData(seqRes);
        const seqData = Array.isArray(seqDataRaw) ? seqDataRaw : [];

        console.log('[DEBUG] studentsData extracted:', studentsData);
        console.log('[DEBUG] gradesData extracted:', gradesData.length, gradesData);
        console.log('[DEBUG] seqData extracted:', seqData.length, seqData);

        setDebugInfo(
          `school: ${schoolId} | year: ${yearId} | ` +
          `students: ${JSON.stringify(studentsData).slice(0, 200)} | ` +
          `grades: ${gradesData.length} items | ` +
          `sequences: ${seqData.length} items`
        );

        setStats({
          totalStudents: studentsData.total || studentsData.total_students || studentsData.count || 0,
          totalGrades: gradesData.length,
          avgGeneral: gradesData.length > 0 
            ? gradesData.reduce((sum: number, g: any) => sum + (g.value || g.score || 0), 0) / gradesData.length 
            : 0,
          sequences: seqData.length,
        });

        // Grade distribution
        const distribution = [
          { range: '0-5', count: 0, color: '#ef4444' },
          { range: '5-8', count: 0, color: '#f97316' },
          { range: '8-10', count: 0, color: '#f59e0b' },
          { range: '10-12', count: 0, color: '#84cc16' },
          { range: '12-14', count: 0, color: '#06b6d4' },
          { range: '14-16', count: 0, color: '#3b82f6' },
          { range: '16-20', count: 0, color: '#10b981' },
        ];

        gradesData.forEach((g: any) => {
          const val = g.value || g.score || 0;
          if (val < 5) distribution[0].count++;
          else if (val < 8) distribution[1].count++;
          else if (val < 10) distribution[2].count++;
          else if (val < 12) distribution[3].count++;
          else if (val < 14) distribution[4].count++;
          else if (val < 16) distribution[5].count++;
          else distribution[6].count++;
        });

        setGradeDistribution(distribution);

        // Try to load rankings for top students (filtré par année)
        try {
          const rankingRes = await apiService.get('/grades/honor-board', { params }).catch(() => ({ data: null }));
          const hb = extractData(rankingRes);
          const top: any[] = [];
          if (hb?.felicitations) top.push(...hb.felicitations.slice(0, 3));
          if (hb?.encouragements && top.length < 5) top.push(...hb.encouragements.slice(0, 5 - top.length));
          setTopStudents(top);
        } catch (e) { setTopStudents([]); }

        // Subject averages — charger les classes filtrées par école/année
        try {
          const classesRes = await apiService.get('/classes', { params }).catch(() => ({ data: [] }));
          const classesRaw = extractData(classesRes);
          const classes = Array.isArray(classesRaw) ? classesRaw : [];
          console.log('[DEBUG] classes:', classes.length, classes);
          if (classes.length > 0) {
            const avgRes = await apiService.get(`/grades/averages/${classes[0].id}`, { params }).catch(() => ({ data: null }));
            const avgData = extractData(avgRes);
            setSubjectAverages(avgData?.subject_averages || []);
          }
        } catch (e) { setSubjectAverages([]); }

      } catch (err) {
        console.error('Dashboard load error:', err);
        setDebugInfo(`Error: ${err}`);
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, [schoolId, currentYear, yearLoading]);

  // ── Affectations de l'enseignant (SES classes + matières) ──
  useEffect(() => {
    const loadAssignments = async () => {
      if (!teacherId || !currentYear || !schoolId) { setTeacherAssignments([]); return; }
      try {
        const res: any = await apiService.get('/timetable/assignments', {
          params: { teacher_id: teacherId, academic_year_id: currentYear.id, school_id: schoolId },
        });
        const data = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : Array.isArray(res?.data) ? res.data : []);
        setTeacherAssignments(data);
        setSelectedAssignIdx(0);
      } catch { setTeacherAssignments([]); }
    };
    loadAssignments();
  }, [teacherId, currentYear, schoolId]);

  // ── Top 10 / derniers 10 pour la classe-matière sélectionnée ──
  useEffect(() => {
    const loadTopBottom = async () => {
      if (!currentYear || teacherAssignments.length === 0) { setTopBottom({ top: [], bottom: [] }); return; }
      const a = teacherAssignments[Math.min(selectedAssignIdx, teacherAssignments.length - 1)];
      if (!a) return;
      setTbLoading(true);
      try {
        // Séquence ouverte de l'année (repli : première séquence disponible)
        let sequenceId: number | null = null;
        try {
          const seqRes: any = await apiService.get('/grades/sequences', { params: { academic_year_id: currentYear.id } });
          const seqs = Array.isArray(seqRes) ? seqRes : (Array.isArray(seqRes?.items) ? seqRes.items : Array.isArray(seqRes?.data) ? seqRes.data : []);
          const open = seqs.find((s: any) => s.is_active !== false);
          sequenceId = open ? open.id : (seqs.length ? seqs[0].id : null);
        } catch { /* ignore */ }

        const params: any = { class_id: a.class_id, subject_id: a.subject_id, academic_year_id: currentYear.id };
        if (sequenceId) params.sequence_id = sequenceId;
        const gRes: any = await apiService.get('/grades', { params }).catch(() => []);
        const grades = Array.isArray(gRes) ? gRes : (Array.isArray(gRes?.items) ? gRes.items : Array.isArray(gRes?.data) ? gRes.data : []);

        // Meilleure note par élève (plusieurs compétences/séquences possibles)
        const byStudent = new Map<number, any>();
        grades.forEach((g: any) => {
          const v = Number(g.value ?? g.score ?? 0);
          const prev = byStudent.get(g.student_id);
          if (!prev || v > Number(prev._v ?? 0)) byStudent.set(g.student_id, { ...g, _v: v });
        });
        const sorted = Array.from(byStudent.values())
          .map((g: any) => ({
            id: g.student_id,
            name: g.student_name || `${g.last_name || ''} ${g.first_name || ''}`.trim() || '—',
            matricule: g.matricule || '—',
            score: g._v,
          }))
          .sort((x, y) => y.score - x.score);
        setTopBottom({ top: sorted.slice(0, 10), bottom: sorted.slice(-10).reverse() });
      } catch { setTopBottom({ top: [], bottom: [] }); }
      finally { setTbLoading(false); }
    };
    loadTopBottom();
  }, [teacherAssignments, selectedAssignIdx, currentYear]);

  const statCards = [
    { label: t.totalStudents, value: stats.totalStudents.toString(), icon: Users, color: 'bg-blue-500', trend: '' },
    { label: t.totalGrades, value: stats.totalGrades.toString(), icon: BookOpen, color: 'bg-emerald-500', trend: '' },
    { label: t.avgGeneral, value: stats.avgGeneral.toFixed(2), icon: TrendingUp, color: 'bg-amber-500', trend: '/20' },
    { label: t.sequences, value: stats.sequences.toString(), icon: Calendar, color: 'bg-purple-500', trend: '' },
  ];

  const hasAnyData = stats.totalStudents > 0 || stats.totalGrades > 0 || stats.sequences > 0;

  // 🔧 Écran de chargement initial (profil + année scolaire)
  if (yearLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // 🔧 Pas d'établissement
  if (!schoolId) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900">{t.noSchool}</h2>
        </div>
      </div>
    );
  }

  // 🔧 Pas d'année scolaire active
  if (!currentYear) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900">{t.noYear}</h2>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header avec info école + année */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-200">
             {t.schoolLabel}: {`#${schoolId || user?.school_id || '-'}`}
          </span>
          <span className="px-2 py-1 bg-green-50 text-green-700 rounded-md border border-green-200">
            {t.yearLabel}: {currentYear?.label}
          </span>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((s, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 ${s.color} rounded-xl flex items-center justify-center shadow-sm`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              {s.trend && <span className="text-xs text-gray-400">{s.trend}</span>}
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Debug info (visible en dev, masquer en prod si besoin) */}
      {debugInfo && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 font-mono">
          <strong>Debug:</strong> {debugInfo}
        </div>
      )}

      {/* No data hint */}
      {!hasAnyData && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          {t.noDataHint}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grade Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#1e3a8a]" />
            {t.distribution}
          </h3>
          {gradeDistribution.length > 0 && gradeDistribution.some(d => d.count > 0) ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gradeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {gradeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">{t.noData}</div>
          )}
        </div>

        {/* Top Students */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#1e3a8a]" />
            {t.topStudents}
          </h3>
          {topStudents.length > 0 ? (
            <div className="space-y-3">
              {topStudents.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700' : 
                    i === 1 ? 'bg-gray-100 text-gray-700' : 
                    i === 2 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{s.name}</p>
                    <p className="text-xs text-gray-500">{s.matricule} — {s.class_name}</p>
                  </div>
                  <span className="text-lg font-bold text-[#1e3a8a]">{s.average.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">{t.noData}</div>
          )}
        </div>

        {/* Subject Averages */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#1e3a8a]" />
            {t.bySubject}
          </h3>
          {subjectAverages.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subjectAverages}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="subject_name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis domain={[0, 20]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: number) => [value.toFixed(2), 'Moyenne']} />
                  <Bar dataKey="average" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">{t.noData}</div>
          )}
        </div>
      </div>

      {/* ── Top 10 / derniers 10 — classes & matières attribuées à l'enseignant ── */}
      {teacherAssignments.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1e3a8a]" />
              {t.myClasses}
            </h3>
            <select
              value={selectedAssignIdx}
              onChange={(e) => setSelectedAssignIdx(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] max-w-xs"
            >
              {teacherAssignments.map((a: any, i: number) => (
                <option key={`${a.class_id}-${a.subject_id}-${i}`} value={i}>
                  {a.class_name || `Classe #${a.class_id}`} — {a.subject_name || `Matière #${a.subject_id}`}
                </option>
              ))}
            </select>
          </div>
          {tbLoading ? (
            <div className="h-48 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RankTable title={t.top10} titleClass="text-emerald-700" rankClass="text-emerald-600" rows={topBottom.top} noData={t.noData} />
              <RankTable title={t.bottom10} titleClass="text-red-700" rankClass="text-red-500" rows={topBottom.bottom} noData={t.noData} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}